import { StreamVideoClient, CallingState, hasAudio, hasVideo } from '@stream-io/video-client'
import EventBus from '@/libs/AppEventBus'
import api from '@/libs/axios'

// How long the SDK keeps trying to reconnect a dropped call before giving up (RECONNECTING_FAILED).
// Slow / unstable networks need a generous window; the SDK default (0) retries forever.
const CALL_DISCONNECTION_TIMEOUT_SECONDS = 60
// Join retries on flaky networks (SDK default is 3).
const CALL_MAX_JOIN_RETRIES = 5

class GetStreamService {
    client = null
    isInitialized = false
    initializing = null
    currentUser = null
    connectionState = true
    endpoint = '/communication/'
    callsSubscription = null
    clientUnsubscribers = []
    // cid -> unsubscribe of the ringing call's callingState$ subscription
    ringingCalls = new Map()

    /**
     * Initialize GetStream client on application load.
     * Safe to call many times: the client is created once per logged in doctor.
     */
    async initialize() {
        if (this.isClientInitialized()) return true
        if (!this.initializing) {
            this.initializing = this.createClient().finally(() => {
                this.initializing = null
            })
        }
        return this.initializing
    }

    async createClient() {
        try {
            const userData = this.getUserData()
            if (!userData?.doctor) {
                console.warn('No doctor user data found, GetStream initialization skipped')
                return false
            }

            const apiKey = process.env.VUE_APP_GETSTREAM_API_KEY
            if (!apiKey) {
                console.error('GetStream API key not found in environment variables')
                return false
            }

            const user = {
                id: userData.doctor.id?.toString(),
                name: userData.title ? userData.title + ' ' + userData.name : userData.name,
                image: userData.avatar || null,
                role: userData.role || 'user'
            }

            // The token cached from login is used first; the SDK calls tokenProvider
            // whenever it needs a new one (expired / missing token).
            const clientOptions = {
                apiKey,
                user,
                tokenProvider: () => this.fetchToken(),
                options: {
                    // auto-reject a second incoming call while the doctor is already in a call
                    rejectCallWhenBusy: true
                }
            }
            if (userData.doctor.get_stream_token) {
                clientOptions.token = userData.doctor.get_stream_token
            }

            this.client = new StreamVideoClient(clientOptions)
            this.currentUser = user

            this.setupIncomingCallListener()
            this.setupConnectivityListener()
            this.setupMissedCallListener()

            this.isInitialized = true
            return true
        } catch (error) {
            console.error('Failed to initialize GetStream client:', error)
            return false
        }
    }

    /**
     * Get user data from localStorage
     */
    getUserData() {
        try {
            const userData = localStorage.getItem('userData')
            return userData ? JSON.parse(userData) : null
        } catch (error) {
            console.error('Failed to parse user data:', error)
            return null
        }
    }

    /**
     * Fetch a fresh GetStream token from the backend (used as the SDK tokenProvider, so it must throw on failure)
     */
    async fetchToken() {
        const response = await api.get(this.endpoint + 'get-token')
        const token = response?.data?.data?.token
        if (!token) {
            throw new Error('GetStream token missing in response')
        }
        return token
    }

    /**
     * Set up incoming call listener
     */
    setupIncomingCallListener() {
        if (!this.client) return

        this.callsSubscription = this.client.state.calls$.subscribe((calls) => {
            calls.forEach((call) => {
                if (!call.isCreatedByMe && call.state.callingState === CallingState.RINGING && !this.ringingCalls.has(call.cid)) {
                    this.handleIncomingCall(call)
                }
            })
        })
    }

    async handleIncomingCall(call) {
        let stopped = false
        const stopRinging = () => {
            if (stopped) return
            stopped = true
            this.ringingCalls.get(call.cid)?.()
            this.ringingCalls.delete(call.cid)
            EventBus.emit('stop-call-notification', { id: call.id })
        }

        const subscription = call.state.callingState$.subscribe((state) => {
            // Calling state changed from ringing to other state (accepted, rejected, cancelled, timed out)
            if (state !== CallingState.RINGING) stopRinging()
        })
        if (stopped) {
            subscription.unsubscribe()
            return
        }
        this.ringingCalls.set(call.cid, () => subscription.unsubscribe())

        // Calls created by the portal / apps carry the ids as custom data; the API is the source of truth.
        const custom = call.state.custom || {}
        const notification = {
            avatar: call.state.createdBy?.image,
            name: call.state.createdBy?.name,
            id: call.id,
            chatId: custom.chat_id ?? null,
            appointmentId: custom.appointment_id ?? null,
            call: call
        }

        try {
            const response = await api.get(this.endpoint + call.id + '/show-call')
            notification.chatId = response.data.data.chat_id ?? notification.chatId
            notification.appointmentId = response.data.data.appointment_id ?? notification.appointmentId
        } catch (error) {
            console.error('Failed to get incoming call detail:', error)
        }

        if (!stopped && call.state.callingState === CallingState.RINGING) {
            EventBus.emit('show-call-notification', notification)
        }
    }

    /**
     * Set up connectivity listener
     */
    setupConnectivityListener() {
        if (!this.client) return

        const unsubscribe = this.client.on('connection.changed', (event) => {
            this.connectionState = event.online

            EventBus.emit('gs-connection-state-changed', {
                online: this.connectionState
            })
        })
        this.clientUnsubscribers.push(unsubscribe)
    }

    /**
     * Set up missed call listener
     */
    setupMissedCallListener() {
        if (!this.client) return

        const unsubscribe = this.client.on('call.missed', (event) => {
            EventBus.emit('show-toast', {
                severity: 'error',
                summary: '',
                detail: 'Missed call from ' + (event.user?.name ?? 'patient')
            })
        })
        this.clientUnsubscribers.push(unsubscribe)
    }

    /**
     * Reconnection settings for slow / unstable networks
     */
    applyCallResilience(call) {
        call.setDisconnectionTimeout(CALL_DISCONNECTION_TIMEOUT_SECONDS)
    }

    /**
     * Create a new call
     */
    async createCall(callType, room, paitnetUserId, appointmentId, chatId) {
        if (callType === undefined) callType = 'default'
        if (!this.client) {
            throw new Error('GetStream client not initialized')
        }

        // one Call instance = one call flow
        const call = this.client.call(callType, room)
        this.applyCallResilience(call)

        try {
            await call.getOrCreate({
                ring: true,
                video: true,
                data: {
                    members: [{ user_id: this.currentUser.id }, { user_id: paitnetUserId }],
                    custom: {
                        appointment_id: appointmentId,
                        chat_id: chatId
                    }
                }
            })

            // Join the call to get binding methods
            await call.join({ create: false, maxJoinRetries: CALL_MAX_JOIN_RETRIES })

            return call
        } catch (error) {
            console.error('Failed to create call:', error)
            await this.leaveCall(call)
            throw error
        }
    }

    /**
     * Accept incoming call
     */
    async acceptCall(callId, callType = 'default') {
        if (!this.client) {
            throw new Error('GetStream client not initialized')
        }

        // Reuse the ringing instance; a second instance would leave the ringing one to time out as "missed".
        const call = this.client.call(callType, callId, { reuseInstance: true })
        this.applyCallResilience(call)

        try {
            // join() accepts a ringing call itself; accept explicitly when the instance is not ringing anymore
            if (!call.ringing) {
                await call.accept()
            }
            await call.join({ create: false, maxJoinRetries: CALL_MAX_JOIN_RETRIES })
            return call
        } catch (error) {
            console.error('Failed to accept call:', error)
            await this.leaveCall(call)
            throw error
        }
    }

    /**
     * Reject incoming call
     */
    async rejectCall(callId, callType = 'default') {
        if (!this.client) {
            throw new Error('GetStream client not initialized')
        }

        const call = this.client.call(callType, callId, { reuseInstance: true })
        if (call.state.callingState === CallingState.RINGING) {
            await this.leaveCall(call, { reject: true, reason: 'decline' })
            return
        }
        // not the ringing instance (or not ringing anymore): leave() would not send the reject
        try {
            await call.reject('decline')
        } catch (error) {
            console.error('Failed to reject call:', error)
        }
        await this.leaveCall(call)
    }

    /**
     * Cancel the ring of an outgoing call. The caller is already JOINED (not RINGING),
     * where leave({ reject: true }) would not tell the callee to stop ringing.
     */
    async cancelRing(call) {
        if (!call || call.state.callingState === CallingState.LEFT) return
        try {
            await call.reject('cancel')
        } catch (error) {
            console.error('Failed to cancel call:', error)
        }
    }

    /**
     * Leave (or reject / cancel) a call; safe to call on a call that already ended
     */
    async leaveCall(call, options = {}) {
        if (!call || call.state.callingState === CallingState.LEFT) return
        try {
            await call.leave(options)
        } catch (error) {
            console.error('Failed to leave call:', error)
        }
    }

    // Update Call
    async updateCall(callId, data) {
        try {
            return await api.post(this.endpoint + `${callId}/update-call`, data)
        } catch (error) {
            console.error('Failed to update call:', error)
            return null
        }
    }

    /**
     * Get client instance
     */
    getClient() {
        return this.client
    }

    /**
     * Get current user
     */
    getCurrentUser() {
        return this.currentUser
    }

    /**
     * Get connection state
     */
    getConnectionState() {
        return this.connectionState
    }

    /**
     * Check if client is initialized
     */
    isClientInitialized() {
        return this.isInitialized && this.client !== null
    }

    /**
     * Disconnect client (logout)
     */
    async disconnect() {
        // logout while the client is still being created
        if (this.initializing) {
            await this.initializing.catch(() => {})
        }
        this.callsSubscription?.unsubscribe()
        this.callsSubscription = null
        this.ringingCalls.forEach((unsubscribe) => unsubscribe())
        this.ringingCalls.clear()
        this.clientUnsubscribers.forEach((unsubscribe) => unsubscribe?.())
        this.clientUnsubscribers = []

        if (this.client) {
            const client = this.client
            this.client = null
            this.isInitialized = false
            this.currentUser = null
            this.connectionState = true
            try {
                await client.disconnectUser()
            } catch (error) {
                console.error('Failed to disconnect GetStream client:', error)
            }
        }
    }

    /**
     * get Participant mic status
     */
    hasAudio(participant) {
        return hasAudio(participant)
    }

    /**
     * get Participant camera status
     */
    hasVideo(participant) {
        return hasVideo(participant)
    }
}

// Create singleton instance
const getStreamService = new GetStreamService()

export default getStreamService
